import React from 'react';
import styled from 'styled-components/macro';

import modularScale from 'styles/helpers/modular-scale';
import { defaultButton } from 'styles/base-classes';
import Anchor from 'components/base/Anchor/Anchor';
import Content from 'components/styled/Content/Content';
import ErrorDetails from 'components/utility/ErrorDetails/ErrorDetails';

const WhiteBackground = styled('div')`
  padding: ${({ theme }): string => theme.spacing.enormous} 0;
  text-align: center;
  flex: 1 1 auto;
  background: white;
  color: ${({ theme }): string => theme.NightBlue};
`;

const Wrapper = styled('div')`
  width: 30em;
  max-width: calc(100% - (${modularScale(0)} * 2));
  margin: 0 auto;
`;

const H1 = styled('h1')`
  font-size: ${modularScale(3)};
  color: inherit;

  @media screen and (min-width: 30em) {
    font-size: ${modularScale(5)};
  }
`;

const Paragraph = styled('p')`
  max-width: 17.5em;
  margin: 1em auto ${({ theme }): string => theme.spacing.large};
`;

const Button = styled('button')`
  ${defaultButton};
  margin-bottom: ${({ theme }): string => theme.spacing.xlarge};
`;

const StyledAnchor = styled(Anchor)`
  display: inline-block;
  margin-bottom: ${({ theme }): string => theme.spacing.xlarge};
`;

export const ErrorMessage = styled('p')`
  font-size: ${modularScale(-1)};
  color: ${({ theme }): string => theme.CasperGrey};
`;

interface IErrorContentProps {
  /** An instance of the native Error object */
  error: Error;
  /** An object with additional error information, like a stack trace, or request headers. */
  errorInfo?: string | Record<string, unknown>;
  /** Status code of the error. Based on this the component renders different content */
  status?: number;
  /** Show or hide the refresh button */
  showRefreshButton?: boolean;
}

/**
 * This component renders the UI for page errors. It changes the
 * render based on the status code. We have a default and 404 error message.
 */
const ErrorContent: React.FC<IErrorContentProps> = ({
  error,
  errorInfo,
  status,
  showRefreshButton = true,
}) => {
  const renderErrorMessage = (): JSX.Element => {
    if (status === 404) {
      return (
        <>
          <H1>We can’t seem to find the page you’re looking for&hellip;</H1>
          <Paragraph>
            This resource was either deleted or it has never existed. Please
            check your URL or try searching.
          </Paragraph>
          <StyledAnchor to="/search">Go to search.</StyledAnchor>
        </>
      );
    }

    return (
      <>
        <H1>Something went wrong&hellip;</H1>
        <Paragraph>
          An error occurred, please try to refresh the page or{' '}
          <Anchor to="/glossary/general-information/contact">
            contact Opus Support.
          </Anchor>
        </Paragraph>
        {showRefreshButton && (
          <Button onClick={(): void => window.location.reload()}>
            Refresh page
          </Button>
        )}
      </>
    );
  };

  return (
    <Content data-pw="error-content">
      <WhiteBackground>
        <Wrapper>
          {renderErrorMessage()}
          <ErrorDetails error={error} errorInfo={errorInfo} />
        </Wrapper>
      </WhiteBackground>
    </Content>
  );
};

export default ErrorContent;
